import Component from '@bythepixel/component-loader';

const IO = {
  SENTINEL_ROOT_MARGIN: '0% 0% -99% 0%',
  VIEWPORT_ROOT_MARGIN: '-1% 0% -90% 0%',
  VIEWPORT_THRESHOLD: 0,
  RESUME_OBSERVE_DELAY_MS: 500,
};

const UI = {
  SECTION_VISIBILITY_EXTRA_PX: 10,
  SUB_NAV_SCROLL_DELAY_MS: 500,
  HALF_WINDOW_DIVISOR: 2,
};

const DEFAULTS = {
  activeLinkClass: 'section-sticky-sub-nav__link--current',
  sections: '[data-sticky-sub-nav-section]',
  sentinel: 'section-sticky-sub-nav__sentinel',
  stickySubNav: 'section-sticky-sub-nav__nav',
  stickySubNavStickyClass: 'section-sticky-sub-nav__nav--sticky',
  stickySubNavLinks: 'section-sticky-sub-nav__link',
  stickySubNavSections: '[data-sticky-sub-nav-sections]',
  getNavLinkSelector: (id) => `[href="#${id}"]`,
};

export default class SectionStickySubNav extends Component {
  static selector = '[data-comp-sticky-sub-nav]';

  constructor(el, options = DEFAULTS) {
    super(el, options);
    this.$container = el;
    this.links = el.querySelectorAll(`.${options.stickySubNavLinks}`);
    this.sections = this.getSectionsToObserve();
    this.sentinel = el.querySelector(`.${options.sentinel}`);
    this.stickySubNav = el.querySelector(`.${options.stickySubNav}`);
    this.currentLink = el.querySelector(`.${options.activeLinkClass}`);

    this.sentinelObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          this.stickySubNav.classList.add(options.stickySubNavStickyClass);
        } else {
          this.stickySubNav.classList.remove(options.stickySubNavStickyClass);
        }
      },
      {
        threshold: IO.VIEWPORT_THRESHOLD,
        rootMargin: IO.SENTINEL_ROOT_MARGIN,
      },
    );

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          this.currentLink = this.$container.querySelector(
            this.$options.getNavLinkSelector(sectionId),
          );
          this.events();
        }
      },
      {
        threshold: IO.VIEWPORT_THRESHOLD,
        rootMargin: IO.VIEWPORT_ROOT_MARGIN,
      },
    );

    this.init();
  }

  init() {
    const { hash } = window.location;
    const section = document.getElementById(hash.substring(1, hash.length));

    if (section) {
      section.scrollIntoView();
    }

    this.clickEvents();
    this.observeSentinel();
    this.observeViewport();
  }

  clickEvents() {
    this.links.forEach((link) => {
      link.addEventListener('click', (e) => {
        // Workaround for a bug in chrome, this prevents a scroll bug that is caused by anchor links and the intersection observerAPI
        e.preventDefault();
        const { href } = link;
        const hash = href.substring(href.indexOf('#'), href.length);
        const id = hash.substring(1, hash.length);

        // Temporarily stop observing so the click event can dictate the active link
        this.unobserveViewport();

        this.currentLink = this.$container.querySelector(
          this.$options.getNavLinkSelector(id),
        );
        this.styleLinks();

        // Ensure the section top is loaded beneath the sticky nav for visibility
        const subNavHeight = this.stickySubNav.getBoundingClientRect().bottom;
        const yOffset = -subNavHeight - UI.SECTION_VISIBILITY_EXTRA_PX;
        const element = document.getElementById(id);
        const y = element.getBoundingClientRect().top + window.scrollY + yOffset;

        window.scrollTo({
          top: y,
          behavior: 'smooth',
        });

        setTimeout(() => {
          this.observeViewport();
        }, IO.RESUME_OBSERVE_DELAY_MS);
      });
    });
  }

  events() {
    if (this.currentLink) {
      this.styleLinks();
      this.subNavScroll();
    }
  }

  observeSentinel() {
    this.sentinelObserver.observe(this.sentinel);
  }

  observeViewport() {
    this.sections.forEach((section) => {
      this.observer.observe(section);
    });
  }

  unobserveViewport() {
    this.sections.forEach((section) => {
      this.observer.unobserve(section);
    });
  }

  styleLinks() {
    this.currentLink.classList.add(this.$options.activeLinkClass);
    this.links.forEach((link) => {
      if (
        link !== this.currentLink
        && link.classList.contains(this.$options.activeLinkClass)
      ) {
        link.classList.remove(this.$options.activeLinkClass);
      }
    });
  }

  subNavScroll() {
    setTimeout(() => {
      const { offsetWidth, offsetLeft } = this.currentLink;
      const innerWidth = this.$container.offsetWidth;
      const halfWindowWidth = innerWidth / UI.HALF_WINDOW_DIVISOR;

      if (offsetLeft > halfWindowWidth) {
        const linkCenter = (innerWidth - offsetWidth) / UI.HALF_WINDOW_DIVISOR;
        const scrollAmount = offsetLeft - linkCenter;
        this.$container.scroll({
          left: scrollAmount,
          behavior: 'smooth',
        });
      } else {
        this.$container.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }
    }, UI.SUB_NAV_SCROLL_DELAY_MS); // Needed to prevent conflicts with the click to scroll functionality
  }

  getSectionsToObserve() {
    const { sections } = this.$options;
    return Array.from(document.querySelectorAll(sections)).filter(
      (section) => !!this.$container.querySelector(
        this.$options.getNavLinkSelector(section.id),
      ),
    );
  }
}
